import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Search, 
  Filter, 
  Users, 
  TrendingUp,
  MapPin,
  Target,
  Award,
  Activity,
  UserPlus,
  ArrowLeft
} from "lucide-react";
import { POSITIONS, getPositionLabel } from "@/utils/positions";

const PlayerPool = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const matchId = searchParams.get('matchId');
  
  const [players, setPlayers] = useState([]);
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPosition, setSelectedPosition] = useState("all");
  const [selectedCity, setSelectedCity] = useState("all");
  const [selectedDistrict, setSelectedDistrict] = useState("all");
  const [minPoints, setMinPoints] = useState("");
  const [maxPoints, setMaxPoints] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showPlayerDetail, setShowPlayerDetail] = useState(false);
  const [showAddPlayerDialog, setShowAddPlayerDialog] = useState(false);
  const [playerToAdd, setPlayerToAdd] = useState(null);
  const [selectedAddPosition, setSelectedAddPosition] = useState("");
  const [adding, setAdding] = useState(false);

  // Turkish cities (major ones)
  const cities = ["all", "İstanbul", "Ankara", "İzmir", "Bursa", "Antalya", "Adana", "Konya", "Gaziantep", "Kayseri"];

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedPosition, selectedCity, selectedDistrict, minPoints, maxPoints, players]);

  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/players/search`);
      const data = response.data || [];
      setPlayers(data);
      setFilteredPlayers(data);
    } catch (error) {
      console.error("Error fetching players:", error);
      toast.error("Oyuncular yüklenemedi");
      setPlayers([]);
      setFilteredPlayers([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    // Guard against null/undefined players
    if (!players || !Array.isArray(players)) {
      setFilteredPlayers([]);
      return;
    }

    let filtered = [...players];

    // Search by name
    if (searchTerm) {
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by position
    if (selectedPosition && selectedPosition !== "all") {
      filtered = filtered.filter(p => p.position === selectedPosition);
    }

    // Filter by city
    if (selectedCity && selectedCity !== "all") {
      filtered = filtered.filter(p => p.city === selectedCity);
    }

    // Filter by district
    if (selectedDistrict && selectedDistrict !== "all") {
      filtered = filtered.filter(p => p.district === selectedDistrict);
    }

    // Filter by points
    if (minPoints) {
      filtered = filtered.filter(p => (p.record?.totalPoints || 0) >= parseInt(minPoints));
    }
    if (maxPoints) {
      filtered = filtered.filter(p => (p.record?.totalPoints || 0) <= parseInt(maxPoints));
    }

    setFilteredPlayers(filtered);
  };

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    setShowPlayerDetail(true);
  };

  const handleAddToMatch = (player) => {
    setPlayerToAdd(player);
    setSelectedAddPosition("");
    setShowAddPlayerDialog(true);
  };

  const confirmAddToMatch = async () => {
    if (!selectedAddPosition) {
      toast.error("Lütfen bir pozisyon seçin");
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/matches/${matchId}/add-player`, {
        playerId: playerToAdd.id,
        position: selectedAddPosition.split('-')[0],
        detailedPosition: selectedAddPosition
      });
      
      toast.success(`${playerToAdd.name} maça eklendi!`);
      setShowAddPlayerDialog(false);
      
      // Navigate back to match detail
      navigate(`/matches/${matchId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Oyuncu eklenemedi");
    } finally {
      setAdding(false);
    }
  };

  const getPositionBadgeColor = (position) => {
    const colors = {
      goalkeeper: "bg-yellow-100 text-yellow-800",
      defender: "bg-blue-100 text-blue-800",
      midfielder: "bg-green-100 text-green-800",
      forward: "bg-red-100 text-red-800"
    };
    return colors[position] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[80vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Oyuncular yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          {matchId && (
            <Button 
              variant="outline" 
              onClick={() => navigate(`/matches/${matchId}`)}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Maç Detayına Dön
            </Button>
          )}
          <h1 className="text-4xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent mb-2">
            Oyuncu Havuzu
            {matchId && <span className="text-xl ml-2 text-gray-600">(Maça Oyuncu Seç)</span>}
          </h1>
          <p className="text-gray-600">
            {matchId ? "Maçına eklemek için oyuncu seç" : "Takımın için en uygun oyuncuları bul"}
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Oyuncu ara..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Position Filter */}
              <div>
                <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pozisyon" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Pozisyonlar</SelectItem>
                    <SelectItem value="goalkeeper">Kaleci</SelectItem>
                    <SelectItem value="defender">Defans</SelectItem>
                    <SelectItem value="midfielder">Orta Saha</SelectItem>
                    <SelectItem value="forward">Forvet</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* City Filter */}
              <div>
                <Select value={selectedCity} onValueChange={setSelectedCity}>
                  <SelectTrigger>
                    <SelectValue placeholder="İl" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm İller</SelectItem>
                    {cities.filter(c => c !== "all").map(city => (
                      <SelectItem key={city} value={city}>{city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* District Filter */}
              <div>
                <Input
                  placeholder="İlçe"
                  value={selectedDistrict === "all" ? "" : selectedDistrict}
                  onChange={(e) => setSelectedDistrict(e.target.value || "all")}
                />
              </div>

              {/* Min Points */}
              <div>
                <Input
                  type="number"
                  placeholder="Min Puan"
                  value={minPoints}
                  onChange={(e) => setMinPoints(e.target.value)}
                />
              </div>

              {/* Max Points */}
              <div>
                <Input
                  type="number"
                  placeholder="Max Puan"
                  value={maxPoints}
                  onChange={(e) => setMaxPoints(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                <span className="font-semibold">{filteredPlayers?.length || 0}</span> oyuncu bulundu
              </p>
              {(searchTerm || selectedPosition !== "all" || selectedCity !== "all" || selectedDistrict !== "all" || minPoints || maxPoints) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSearchTerm("");
                    setSelectedPosition("all");
                    setSelectedCity("all");
                    setSelectedDistrict("all");
                    setMinPoints("");
                    setMaxPoints("");
                  }}
                >
                  Filtreleri Temizle
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Players Grid */}
        {!filteredPlayers || filteredPlayers.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center text-gray-500">
                <Users className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Oyuncu bulunamadı</p>
                <p className="text-sm mt-2">Filtreleri değiştirmeyi deneyin</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredPlayers?.map((player) => (
              <Card 
                key={player.id}
                className="hover:shadow-lg transition-shadow"
              >
                <CardContent className="pt-6">
                  {/* Player Avatar */}
                  <div className="flex flex-col items-center mb-4 cursor-pointer" onClick={() => handlePlayerClick(player)}>
                    <Avatar className="h-20 w-20 mb-3">
                      <AvatarImage src={player.profileImage} />
                      <AvatarFallback className="text-2xl">{player.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <h3 className="font-bold text-lg text-center">{player.name}</h3>
                    {player.position && (
                      <Badge className={`mt-2 ${getPositionBadgeColor(player.position)}`}>
                        {player.position === 'goalkeeper' ? 'Kaleci' :
                         player.position === 'defender' ? 'Defans' :
                         player.position === 'midfielder' ? 'Orta Saha' : 'Forvet'}
                      </Badge>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="space-y-2 mb-4" onClick={() => handlePlayerClick(player)}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Award className="h-4 w-4" />
                        Puan
                      </span>
                      <span className="font-semibold text-emerald-600">
                        {player.record?.totalPoints || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Activity className="h-4 w-4" />
                        Kondisyon
                      </span>
                      <span className="font-semibold">
                        {player.kondisyon || 50}/100
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 flex items-center gap-1">
                        <Target className="h-4 w-4" />
                        Maç
                      </span>
                      <span className="font-semibold">
                        {player.record?.matchesPlayed || 0}
                      </span>
                    </div>
                  </div>

                  {/* Quick Stats */}
                  <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t mb-3" onClick={() => handlePlayerClick(player)}>
                    <div>
                      <p className="text-xs text-gray-600">Gol</p>
                      <p className="font-bold text-sm">{player.record?.goals || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Asist</p>
                      <p className="font-bold text-sm">{player.record?.assists || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">MVP</p>
                      <p className="font-bold text-sm">{player.record?.mvp || 0}</p>
                    </div>
                  </div>

                  {/* Add to Match Button */}
                  {matchId && (
                    <Button
                      className="w-full bg-gradient-to-r from-purple-500 to-purple-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddToMatch(player);
                      }}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Maça Ekle
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Player Detail Dialog */}
      <Dialog open={showPlayerDetail} onOpenChange={setShowPlayerDetail}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Oyuncu Detayları</DialogTitle>
          </DialogHeader>
          {selectedPlayer && (
            <div className="space-y-6">
              {/* Player Header */}
              <div className="flex items-center gap-4">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={selectedPlayer.profileImage} />
                  <AvatarFallback className="text-3xl">{selectedPlayer.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <h2 className="text-2xl font-bold">{selectedPlayer.name}</h2>
                  <p className="text-gray-600">{selectedPlayer.email}</p>
                  {selectedPlayer.position && (
                    <Badge className={`mt-2 ${getPositionBadgeColor(selectedPlayer.position)}`}>
                      {selectedPlayer.position === 'goalkeeper' ? 'Kaleci' :
                       selectedPlayer.position === 'defender' ? 'Defans' :
                       selectedPlayer.position === 'midfielder' ? 'Orta Saha' : 'Forvet'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Stats Overview */}
              <Card>
                <CardHeader>
                  <CardTitle>Genel İstatistikler</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-emerald-50 rounded-lg">
                      <p className="text-2xl font-bold text-emerald-600">
                        {selectedPlayer.record?.totalPoints || 0}
                      </p>
                      <p className="text-sm text-gray-600">Toplam Puan</p>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedPlayer.record?.matchesPlayed || 0}
                      </p>
                      <p className="text-sm text-gray-600">Maç</p>
                    </div>
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <p className="text-2xl font-bold text-purple-600">
                        {selectedPlayer.kondisyon || 50}
                      </p>
                      <p className="text-sm text-gray-600">Kondisyon</p>
                    </div>
                    <div className="text-center p-3 bg-orange-50 rounded-lg">
                      <p className="text-2xl font-bold text-orange-600">
                        {selectedPlayer.record?.mvp || 0}
                      </p>
                      <p className="text-sm text-gray-600">MVP</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Technical Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Teknik Kart</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Technical Skills */}
                    <div>
                      <p className="font-semibold mb-3 text-sm text-gray-600">Teknik Yetenekler</p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Pas</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.passing || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-emerald-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.passing || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Şut</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.shooting || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-emerald-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.shooting || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Top Kontrolü</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.ballControl || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-emerald-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.ballControl || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Dribling</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.dribbling || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-emerald-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.dribbling || 50}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Physical */}
                    <div>
                      <p className="font-semibold mb-3 text-sm text-gray-600">Fiziksel</p>
                      <div className="space-y-2">
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Hız</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.speed || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.speed || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Dayanıklılık</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.stamina || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.stamina || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Çeviklik</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.agility || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.agility || 50}%`}}
                            ></div>
                          </div>
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-1">
                            <span>Güç</span>
                            <span className="font-semibold">{selectedPlayer.technicalCard?.power || 50}</span>
                          </div>
                          <div className="h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-blue-500 rounded-full" 
                              style={{width: `${selectedPlayer.technicalCard?.power || 50}%`}}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Match Record */}
              <Card>
                <CardHeader>
                  <CardTitle>Maç Performansı</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-3xl font-bold text-emerald-600">
                        {selectedPlayer.record?.goals || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Gol</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">
                        {selectedPlayer.record?.assists || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Asist</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-purple-600">
                        {selectedPlayer.record?.saves || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">Kurtarış</p>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-orange-600">
                        {selectedPlayer.record?.mvp || 0}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">MVP</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Player to Match Dialog */}
      <Dialog open={showAddPlayerDialog} onOpenChange={setShowAddPlayerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Oyuncuyu Maça Ekle</DialogTitle>
          </DialogHeader>
          {playerToAdd && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={playerToAdd.profileImage} />
                  <AvatarFallback>{playerToAdd.name?.[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <p className="font-semibold">{playerToAdd.name}</p>
                  <p className="text-sm text-gray-600">
                    {playerToAdd.record?.totalPoints || 0} puan
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Pozisyon Seç</Label>
                <Select value={selectedAddPosition} onValueChange={setSelectedAddPosition}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pozisyon seçin" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px]">
                    {POSITIONS.filter(p => p.value !== 'substitute').map((pos) => (
                      <SelectItem key={pos.value} value={pos.value}>
                        {pos.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Oyuncu seçtiğiniz pozisyonda maça eklenecek ve bildirim alacak.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowAddPlayerDialog(false)}
                  className="flex-1"
                >
                  İptal
                </Button>
                <Button
                  onClick={confirmAddToMatch}
                  disabled={adding || !selectedAddPosition}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-purple-600"
                >
                  {adding ? "Ekleniyor..." : "Maça Ekle"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlayerPool;
